import { getDisplayPrice, getLatestPrice, type ParsedAsset } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import { useCallback, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import Animated from 'react-native-reanimated'
import { DatePicker } from '~/components/DatePicker'
import { ManualPropertyInputPhase } from '~/components/flows/phases/ManualPropertyInputPhase.tsx'
import { type PropertyInputData } from '~/lib/propertyFormSchemas'
import { useAlert } from '~/components/GlobalAlert.tsx'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox'
import { ScrollView } from '~/components/ScrollView'
import { Detail, DisplayValue, Label } from '~/components/typography/Content.tsx'
import { Heading2 } from '~/components/typography/Titles'
import { Button } from '~/components/ui/button.tsx'
import { Input } from '~/components/ui/input'
import { useUpdateAsset } from '~/data/assets/useAssets.ts'
import { getValidDate } from '~/lib/utils.ts'

type UpdatePropertyViewData = {
  details: Partial<PropertyInputData>
  dates: {
    listingDate: string
    saleDate: string
  }
}

export function UpdatePropertyView({ asset }: { asset: ParsedAsset }) {
  const { t } = useTranslations(['property', 'common'])
  const router = useRouter()
  const { showAlert } = useAlert()
  const { mutate: updateAsset, isPending } = useUpdateAsset()

  const relationData = asset?.relations?.[0]?.data
  const relationType = asset?.relations?.[0]?.relation_type ?? 'representation'

  const displayPrice = getDisplayPrice(asset?.prices || [])
  const finalPrice = getLatestPrice(asset?.prices || [], 'final_price')
  const unit = asset?.data?.units?.[0]

  const isProspect = relationType === 'prospect'

  const [formData, setFormData] = useState<UpdatePropertyViewData>(() => ({
    details: {
      relationType: relationType,
      address: asset?.address ?? undefined,
      location: asset?.geom ?? undefined,
      buildingType: asset?.data?.propertyType,
      rooms: unit?.rooms ?? undefined,
      size: unit?.size ?? undefined,
      buildYear: unit?.buildYear ?? undefined,
      notes: relationData?.notes ?? undefined,
      media: asset?.media ?? undefined,
      prices: {
        displayPrice: displayPrice?.price ?? 0,
        finalPrice: finalPrice?.price ?? undefined,
      },
    },
    dates: {
      listingDate: relationData?.listingDate ?? '',
      saleDate: relationData?.saleDate ?? '',
    },
  }))

  const updateDetails = useCallback((details: Partial<PropertyInputData>) => {
    setFormData((prev) => ({
      ...prev,
      details: { ...prev.details, ...details },
    }))
  }, [])

  const updateDates = useCallback((key: 'listingDate' | 'saleDate', value: string) => {
    setFormData((prev) => ({
      ...prev,
      dates: { ...prev.dates, [key]: value },
    }))
  }, [])

  const handleSave = useCallback(() => {
    updateAsset(
      {
        assetId: asset.id,
        assetPayload: {
          prices: formData.details.prices,
          relationData: {
            listingDate: formData.dates.listingDate || undefined,
            saleDate: formData.dates.saleDate || undefined,
            listingLink: asset.source?.sourceWebUrl,
            notes: formData.details.notes,
          },
          media: formData.details.media,
          data: formData.details
            ? {
                propertyType:
                  formData.details.buildingType ?? asset.data?.propertyType ?? 'apartment',
                units: [
                  {
                    type: asset.data?.units?.[0]?.type || 'residential',
                    entrances: asset.data?.units?.[0]?.entrances || 1,
                    floorCount: asset.data?.units?.[0]?.floorCount || 1,
                    rooms: formData.details.rooms,
                    size: formData.details.size,
                    buildYear: formData.details.buildYear,
                    condition: asset.data?.units?.[0]?.condition,
                  },
                ],
              }
            : undefined,
        },
      },
      {
        onSuccess: () => {
          router.replace(`/(authenticated)/property/${asset.id}`)
        },
        onError: (error) => {
          console.error('Error saving property:', error)

          showAlert({
            type: 'error',
            title: t(($) => $.errors.error),
            description: t(($) => $.errors.saveFailed),
          })
        },
      }
    )
  }, [router, showAlert, asset, updateAsset, formData, t])

  return (
    <ScrollView
      className="flex-1 rounded-content"
      offsetTrackHeight
      contentContainerClassName="gap-3"
      footer={
        <Animated.View className="self-center pb-6">
          <Button
            variant="primary"
            size="lg"
            onPress={handleSave}
            loading={isPending}
            disabled={isPending}>
            {t(($) => $.actions.saveChanges)}
          </Button>
        </Animated.View>
      }>
      <ContentBox
        scrollable={false}
        contentContainerClassName="p-6"
        header={
          <BoxActionsHeader variant="default" contentClassName="pb-0">
            <Heading2 className="text-center">{t(($) => $.view.propertyDetails)}</Heading2>
          </BoxActionsHeader>
        }>
        <ManualPropertyInputPhase
          mode="edit"
          data={formData.details}
          onUpdateData={updateDetails}
        />
      </ContentBox>

      {!isProspect && (
        <ContentBox
          scrollable={false}
          contentContainerClassName="gap-6 p-6"
          header={
            <BoxActionsHeader variant="default" contentClassName="pb-0">
              <Heading2 className="text-center">{t(($) => $.view.dates)}</Heading2>
            </BoxActionsHeader>
          }>
          <DatesSection
            relationType={relationType}
            listingDate={formData.dates.listingDate}
            saleDate={formData.dates.saleDate}
            onUpdateListingDate={(value) => updateDates('listingDate', value)}
            onUpdateSaleDate={(value) => updateDates('saleDate', value)}
          />
        </ContentBox>
      )}
    </ScrollView>
  )
}

const maxDate = dayjs().endOf('day').toDate()
const minDate = dayjs().subtract(10, 'year').toDate()

function DatesSection({
  relationType,
  listingDate,
  saleDate,
  onUpdateListingDate,
  onUpdateSaleDate,
}: {
  relationType: string
  listingDate: string
  saleDate: string
  onUpdateListingDate: (value: string) => void
  onUpdateSaleDate: (value: string) => void
}) {
  const { t } = useTranslations('property')
  const currentListingDate = useMemo(() => getValidDate(listingDate), [listingDate])

  const currentSaleDate = useMemo(() => getValidDate(saleDate), [saleDate])

  const existingDateInput = useRef<'listing' | 'sale' | null>(
    currentListingDate ? 'listing' : currentSaleDate ? 'sale' : null
  )

  const [showDaysInput, setShowDaysInput] = useState(!currentSaleDate || !currentListingDate)

  const daysOnMarket = useMemo(() => {
    if (!currentListingDate || !currentSaleDate) {
      return 0
    }

    return dayjs(currentSaleDate).diff(dayjs(currentListingDate), 'day')
  }, [currentListingDate, currentSaleDate])

  const [daysOnMarketInput, _setDaysOnMarketInput] = useState(daysOnMarket.toString())

  const setDaysOnMarketInput = useCallback(
    (text: string) => {
      existingDateInput.current = currentListingDate ? 'listing' : currentSaleDate ? 'sale' : null

      _setDaysOnMarketInput(text)
    },
    [currentListingDate, currentSaleDate]
  )

  return (
    <View className="gap-6">
      <View className="gap-2">
        <Label>{t(($) => $.view.listingDate)}</Label>
        <DatePicker
          maxDate={maxDate}
          minDate={minDate}
          value={currentListingDate ? new Date(currentListingDate) : undefined}
          onDateChange={(date) => {
            const nextListingDate = dayjs(date)

            if (!nextListingDate.isValid()) {
              return
            }

            onUpdateListingDate(nextListingDate.format('YYYY-MM-DD'))

            if (date && !existingDateInput.current) {
              existingDateInput.current = 'listing'
            }

            if (!date && existingDateInput.current === 'listing') {
              existingDateInput.current = null

              if (currentSaleDate) {
                setShowDaysInput(true)
              }
            }

            if (date && currentSaleDate) {
              setShowDaysInput(false)
            }
          }}
        />
        <Detail>{t(($) => $.view.listingDateDescription)}</Detail>
      </View>

      {relationType === 'reference' && (
        <>
          <View className="gap-2">
            <Label>{t(($) => $.flow.saleDate)}</Label>
            <DatePicker
              maxDate={maxDate}
              minDate={currentListingDate || minDate}
              value={currentSaleDate ? new Date(currentSaleDate) : undefined}
              onDateChange={(date) => {
                const nextSaleDate = dayjs(date)

                if (!nextSaleDate.isValid()) {
                  return
                }

                onUpdateSaleDate(nextSaleDate.format('YYYY-MM-DD'))

                if (date && !existingDateInput.current) {
                  existingDateInput.current = 'sale'
                }

                if (!date && existingDateInput.current === 'sale') {
                  existingDateInput.current = null

                  if (currentListingDate) {
                    setShowDaysInput(true)
                  }
                }

                if (date && currentListingDate) {
                  setShowDaysInput(false)
                }
              }}
            />
            <Detail>{t(($) => $.view.saleDateDescription)}</Detail>
          </View>
          {(!!currentListingDate || !!currentSaleDate) && (
            <View className="gap-2">
              <Label>{t(($) => $.view.daysOnMarket)}</Label>
              {!showDaysInput ? (
                <DisplayValue>
                  {daysOnMarket} {t(($) => $.view.days)}
                </DisplayValue>
              ) : (
                <Input
                  placeholder={t(($) => $.view.daysOnMarketPlaceholder)}
                  keyboardType="numeric"
                  value={daysOnMarketInput}
                  onSubmitEditing={() => {
                    if (currentListingDate && !currentSaleDate) {
                      setShowDaysInput(false)
                    }
                  }}
                  onChangeText={(text) => {
                    setDaysOnMarketInput(text)
                    const days = Number.parseInt(text, 10)

                    if (!Number.isNaN(days)) {
                      if (existingDateInput.current === 'sale') {
                        onUpdateListingDate(
                          dayjs(currentListingDate).add(days, 'day').format('YYYY-MM-DD')
                        )
                      } else {
                        onUpdateSaleDate(
                          dayjs(currentSaleDate).subtract(days, 'day').format('YYYY-MM-DD')
                        )
                      }
                    }
                  }}
                />
              )}
            </View>
          )}
        </>
      )}
    </View>
  )
}
